'use client'

import type { ReactNode } from 'react'
import { BlockStack, Banner, Button, InlineStack } from '@shopify/polaris'
import { DashboardMoversSummary } from '@/components/dashboard/DashboardMoversSummary'
import type { DashboardMoversResult } from '@/lib/dashboard/dashboardMovers'
import {
  OVERVIEW_RTO_ATTENTION_PCT,
  OVERVIEW_USAGE_WARNING_PCT,
} from '@/lib/dashboard/overviewInsightThresholds'

type Props = {
  movers: DashboardMoversResult
  shop: string | null
  host: string | null
  dateFrom: string
  dateTo: string
  /** Rule-based signals */
  rtoRatePct: number
  netProfit: number
  usagePercentUsed: number
  usageOrderLimit: number | null
  billingHref: string
}

/**
 * Single prioritized insight region: threshold banners first, then movers (capped).
 */
export function DashboardOverviewInsights({
  movers,
  shop,
  host,
  dateFrom,
  dateTo,
  rtoRatePct,
  netProfit,
  usagePercentUsed,
  usageOrderLimit,
  billingHref,
}: Props) {
  const alerts: ReactNode[] = []

  if (netProfit < 0) {
    alerts.push(
      <Banner key="profit" tone="warning" title="Profit is negative this period">
        <p>Review expenses, returns, and margins in Analytics to find the largest drivers.</p>
      </Banner>
    )
  }

  if (rtoRatePct >= OVERVIEW_RTO_ATTENTION_PCT) {
    alerts.push(
      <Banner key="rto" tone="critical" title="Return rate is high">
        <p>
          Returns are {rtoRatePct.toFixed(1)}% of fulfilled orders (we highlight above{' '}
          {OVERVIEW_RTO_ATTENTION_PCT}%). Check Cities and Couriers for weak lanes.
        </p>
      </Banner>
    )
  }

  if (
    usageOrderLimit != null &&
    usagePercentUsed >= OVERVIEW_USAGE_WARNING_PCT &&
    usagePercentUsed < 100
  ) {
    alerts.push(
      <Banner
        key="usage"
        tone="warning"
        title="Approaching monthly order limit"
        action={{ content: 'View billing', url: billingHref }}
      >
        <p>You have used about {Math.round(usagePercentUsed)}% of orders on your current plan.</p>
      </Banner>
    )
  }

  const showMovers =
    movers.hasComparablePeriod && (movers.improvements.length > 0 || movers.setbacks.length > 0)

  if (alerts.length === 0 && !showMovers) {
    return null
  }

  const analyticsHref =
    shop && host
      ? `/dashboard?shop=${encodeURIComponent(shop)}&host=${encodeURIComponent(host)}&tab=analytics`
      : '/dashboard?tab=analytics'

  return (
    <BlockStack gap="400">
      {alerts}
      {showMovers ? (
        <BlockStack gap="200">
          <DashboardMoversSummary
            movers={movers}
            shop={shop}
            host={host}
            dateFrom={dateFrom}
            dateTo={dateTo}
            maxBulletsPerSection={3}
          />
          <InlineStack>
            <Button variant="plain" url={analyticsHref}>
              Open Analytics for channel and courier detail
            </Button>
          </InlineStack>
        </BlockStack>
      ) : null}
    </BlockStack>
  )
}
